import Device from 'Faster/model/Device';
import DeviceData from 'Faster/model/DeviceData';
import DeviceDataJsonTransformer from 'Faster/serialization/DeviceDataJsonTransformer';
import { default as uuidV4 } from 'uuid/v4';
import StorageEngine from 'Faster/storage/StorageEngine';
import StorageValidation from 'Faster/storage/StorageValidation';

class DeviceStorage {
  private static DEVICE_ID_KEY = 'fstr.device_id';
  private static DEVICE_DATA_KEY = 'fstr.device_data';
  private static PERSISTED_FLAG_KEY = 'fstr.device_persisted';

  public readonly deviceId: string;
  public readonly isNewInstall: boolean;
  private _isRemotelyPersisted: boolean;
  private _data: DeviceData;
  private storage: StorageEngine;

  public constructor(storageEngine: StorageEngine, externalDeviceId?: string) {
    this.storage = storageEngine;
    const storedDeviceId = this.retrieveDeviceId();
    this.deviceId = this.createDeviceId(storedDeviceId, externalDeviceId);
    this.storeDeviceId(this.deviceId);

    this.isNewInstall = null === storedDeviceId;

    this._data = this.retrieveDeviceData();
    this._isRemotelyPersisted = this.storage.retrieve(DeviceStorage.PERSISTED_FLAG_KEY) === 'true';
  }

  public getDevice(): Device | null {
    if (!this._data && !this.deviceId) {
      return null;
    }

    if (!this._data) {
      return new Device(this.deviceId, {});
    }

    return new Device(this.deviceId, this._data);
  }

  public set data(deviceData: DeviceData) {
    this._data = deviceData;
    this.storeDeviceData(deviceData);
  }

  public get data(): DeviceData {
    return this._data;
  }

  public get isRemotelyPersisted(): boolean {
    return this._isRemotelyPersisted;
  }

  public set isRemotelyPersisted(value: boolean) {
    this._isRemotelyPersisted = value;
    this.storage.store(DeviceStorage.PERSISTED_FLAG_KEY, value.toString());
  }

  private createDeviceId(storedDeviceId: string, externalDeviceId: string): string {
    return storedDeviceId || externalDeviceId || uuidV4();
  }

  private retrieveDeviceId(): string | undefined {
    const storedId = this.storage.retrieve(DeviceStorage.DEVICE_ID_KEY);

    if (storedId) {
      return storedId;
    }

    return null;
  }

  private retrieveDeviceData(): DeviceData | undefined {
    const deviceDataJson = this.storage.retrieve(DeviceStorage.DEVICE_DATA_KEY, StorageValidation.validateJson);
    if (!deviceDataJson) {
      return null;
    }

    return JSON.parse(deviceDataJson, DeviceDataJsonTransformer.reviver) as DeviceData;
  }

  private storeDeviceId(deviceId: string): void {
    this.storage.store(DeviceStorage.DEVICE_ID_KEY, deviceId);
  }

  private storeDeviceData(deviceData: DeviceData): void {
    this.storage.store(DeviceStorage.DEVICE_DATA_KEY, JSON.stringify(DeviceDataJsonTransformer.toJSON(deviceData)));
  }
}

export default DeviceStorage;
